import React, { useEffect, useMemo, useState } from 'react';
import type { CFEntity, CFQuery } from '../../types/campaignFinance';
import { useCFEntity, type CFEntityScope } from '../../hooks/useCFEntity';
import CFTransactionsExplorer from './CFTransactionsExplorer';

interface CFExplorerPanelProps {
  /** The searched query — its filters scope all three entity tabs. */
  query: CFQuery;
  cycleLabel?: string;
  /** Forwarded to the explorer: Source / Counterparty cells launch a new search. */
  onEntitySearch?: (kind: 'contributor' | 'committee', name: string) => void;
}

/**
 * General (cross-committee) transactions explorer. Drives the same
 * CFTransactionsExplorer used by the committee dashboard, but scoped by the
 * search filters instead of a single committee — so the general search shares
 * one table implementation with Screen 1.
 */
const CFExplorerPanel: React.FC<CFExplorerPanelProps> = ({ query, cycleLabel, onEntitySearch }) => {
  const contributions = useCFEntity('contributions');
  const expenditures = useCFEntity('expenditures');
  const loans = useCFEntity('loans');
  const [activeTab, setActiveTab] = useState<CFEntity>('contributions');

  const { load: loadContrib } = contributions;
  const { load: loadExp } = expenditures;
  const { load: loadLoans } = loans;

  const isExpendituresMode = query.mode === 'expenditures';

  const scope: CFEntityScope = useMemo(() => ({
    committee_name: query.committee_name || undefined,
    contributor: query.contributor || undefined,
    candidate: query.candidate || undefined,
    contribution_type: query.contribution_type || undefined,
    contributor_type: query.contributor_type || undefined,
    committee_type: query.committee_type || undefined,
    min_amount: query.min_amount || undefined,
    max_amount: query.max_amount || undefined,
    employer: query.employer || undefined,
    year: query.year ? String(query.year) : undefined,
    start_date: query.start_date || undefined,
    end_date: query.end_date || undefined,
    search: query.searchQuery || undefined,
    payee: query.payee || undefined,
    vendor_id: query.vendor_id || undefined,
    expenditure_type: query.expenditure_type || undefined,
    payment_type: query.payment_type || undefined,
    disbursement_type: query.disbursement_type || undefined,
  }), [
    query.committee_name, query.contributor, query.candidate, query.contribution_type,
    query.contributor_type, query.committee_type, query.min_amount, query.max_amount,
    query.employer, query.year, query.start_date, query.end_date, query.searchQuery,
    query.payee, query.vendor_id, query.expenditure_type, query.payment_type, query.disbursement_type,
  ]);

  // Tabs only appear when the search carries a filter that actually BOUNDS that
  // entity's table — an unbounded tab queries the whole cycle and times out.
  // Contributions mode: contributor filters don't scope expenditures/loans, so those
  // tabs need free-text or committee/candidate. Expenditures mode is the mirror image:
  // payee/type filters don't scope contributions, so that tab needs the same.
  const entityTabs: CFEntity[] = useMemo(() => {
    const crossBounding = !!(query.searchQuery || query.committee_name || query.candidate);
    if (isExpendituresMode) {
      return crossBounding ? ['expenditures', 'contributions', 'loans'] : ['expenditures'];
    }
    return crossBounding ? ['contributions', 'expenditures', 'loans'] : ['contributions'];
  }, [isExpendituresMode, query.searchQuery, query.committee_name, query.candidate]);

  useEffect(() => {
    const first: CFEntity = isExpendituresMode ? 'expenditures' : 'contributions';
    if (entityTabs.includes('contributions')) loadContrib(scope, 'none');
    if (entityTabs.includes('expenditures')) loadExp(scope, 'none');
    if (entityTabs.includes('loans')) loadLoans(scope, 'none');
    setActiveTab(first);
  }, [scope, entityTabs, isExpendituresMode, loadContrib, loadExp, loadLoans]);

  return (
    <CFTransactionsExplorer
      activeTab={activeTab}
      onTabChange={setActiveTab}
      entities={{ contributions, expenditures, loans }}
      cycleLabel={cycleLabel}
      tabs={entityTabs}
      onEntitySearch={onEntitySearch}
    />
  );
};

export default CFExplorerPanel;
